import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "../../utils/toast";

import {
  applyAuthResponse,
  login as loginRequest,
  resendVerification,
} from "../../api/auth";
import AuthContext from "../../contexts/AuthCtx";
import AuthLayout from "../../components/UX/AuthLayout";
import Button from "../../components/UX/Button";
import GoogleAuthButton from "../../components/UX/GoogleAuthButton";
import PasswordInput from "../../components/UX/PasswordInput";
import TextInput from "../../components/UX/TextInput";
import { loginFromResponse, getPostAuthPath } from "../../utils/authRedirect";
import { identifyUser } from "../../analytics";

function Login() {
  const AuthCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (AuthCtx.isLoggedIn) {
      navigate(getPostAuthPath(AuthCtx.userStatus), { replace: true });
    }
  }, [AuthCtx.isLoggedIn, AuthCtx.userStatus, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(false);

    try {
      const response = await loginRequest(email.toLowerCase(), password);
      const data = applyAuthResponse(response);
      identifyUser({
        id: data.user?._id,
        email: data.user?.email,
        userStatus: data.user?.userStatus,
        userRole: data.user?.userRole,
      });
      loginFromResponse(AuthCtx, data, navigate);
    } catch (err) {
      if (err.response?.data?.code === "EMAIL_NOT_VERIFIED") {
        setNeedsVerification(true);
        toast.error(
          err.response?.data?.message ||
            "Confirme ton adresse e-mail avant de te connecter."
        );
      } else {
        toast.error(
          err.response?.data?.message || "E-mail ou mot de passe incorrect."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email || resending) return;
    setResending(true);
    try {
      await resendVerification(email.toLowerCase());
      toast.success("Un nouvel e-mail de confirmation a été envoyé.");
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "Impossible de renvoyer l'e-mail. Réessaie dans quelques minutes."
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout
      headerLink={
        <>
          Pas encore de compte ?{" "}
          <Link to="/signup" className="text-primaryText underline">
            S&apos;inscrire
          </Link>
        </>
      }
    >
      <div className="text-center mb-2">
        <h1 className="font-logo text-3xl md:text-4xl text-black mb-3">
          Accède à ton espace personnel
        </h1>
        <p className="font-josefin text-secondaryText text-sm md:text-base">
          Reprends là où tu t&apos;étais arrêté.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <TextInput
          id="login-email"
          type="email"
          placeholder="Adresse e-mail"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setNeedsVerification(false);
          }}
          autoComplete="email"
          required
        />
        <PasswordInput
          id="login-password"
          placeholder="Mot de passe"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        <Button type="submit" disabled={loading}>
          {loading ? "Connexion…" : "Se connecter"}
        </Button>
      </form>

      {needsVerification && (
        <p className="text-center font-josefin text-sm text-secondaryText">
          Tu n&apos;as pas reçu l&apos;e-mail ?{" "}
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="text-primaryText underline disabled:opacity-50"
          >
            {resending ? "Envoi…" : "Renvoyer l'e-mail de confirmation"}
          </button>
        </p>
      )}

      <p className="text-center font-josefin text-sm text-secondaryText">
        <Link to="/forgot-password" className="text-primaryText underline">
          Mot de passe oublié ?
        </Link>
      </p>

      <div className="flex items-center gap-4 my-2">
        <div className="flex-1 h-px bg-greyLight" />
        <span className="font-josefin text-xs uppercase tracking-wider text-secondaryText">
          ou
        </span>
        <div className="flex-1 h-px bg-greyLight" />
      </div>

      <GoogleAuthButton label="Se connecter avec Google" />
    </AuthLayout>
  );
}

export default Login;
